// src/components/Inquiry/items/BlueprintMenu.tsx
//
// Upload / download / remove the PDF drawing on a machined product, from the
// inquiry's item table. This was ninety lines of fetch() and file-input
// plumbing living in the middle of a table row; it is self-contained work on
// one product, so it is its own component.
import { FunctionComponent, useRef, useState, useContext } from "react";
import { Button, Menu, MenuItem } from "@mui/material";
import { ArrowDropDown, PictureAsPdf } from "@mui/icons-material";
import AppStateContext from "contexts/AppStateContext";
import { useToast } from "contexts/ToastContext";
import { apiUpdateProduct } from "util/network/Products";
import { fetchPDFFile } from "util/FileDownload";
import { API_URL } from "util/network/common";
import {
  CNCProductDetailsType,
  SheetProductDetailsType,
} from "types/products/ProductCommandsType";

/** The two product kinds that carry a drawing. */
export type BlueprintDetails = CNCProductDetailsType | SheetProductDetailsType;

type PropsType = {
  productId: number;
  details: BlueprintDetails | undefined;
  /** The product came back changed - hand the new details to the caller. */
  onDetailsChange: (details: BlueprintDetails) => void;
  disabled?: boolean;
};

const BlueprintMenu: FunctionComponent<PropsType> = ({
  productId,
  details,
  onDetailsChange,
  disabled,
}) => {
  const { token } = useContext(AppStateContext);
  const { addToast } = useToast();
  const fileInput = useRef<HTMLInputElement>(null);
  const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null);

  const blueprint = details?.blueprint ?? null;

  // The blueprint is written on the product, not on the line, so the details
  // the caller holds have to be replaced with what came back - otherwise the
  // menu still offers "Upload" for a drawing that is already there.
  const writeBlueprint = (name: string | null) => {
    if (!details) return;
    apiUpdateProduct(token, productId, {
      quantity: details.quantity,
      threads: details.threads,
      comment: details.comment,
      blueprint: name,
    })
      .then((res) => onDetailsChange(res.details as BlueprintDetails))
      .catch((err) =>
        addToast({
          type: "error",
          message: `The drawing could not be saved: ${err}`,
          keep: true,
        })
      );
  };

  const upload = async (files: FileList) => {
    const file = files[0];
    if (!file || file.type !== "application/pdf" || !details) return;

    const body = new FormData();
    body.append("file", file);
    body.append("filename", file.name);

    try {
      const response = await fetch(
        `${API_URL}/v1/ecommerce/products/${productId}/blueprint`,
        { method: "POST", headers: { Authorization: `Bearer ${token}` }, body }
      );
      if (response.status !== 201) throw new Error(await response.text());
      writeBlueprint(await response.text());
    } catch (err) {
      addToast({
        type: "error",
        message: `The drawing could not be uploaded: ${err}`,
        keep: true,
      });
    }
  };

  const remove = async () => {
    try {
      const response = await fetch(
        `${API_URL}/v1/ecommerce/products/${productId}/blueprint`,
        { method: "DELETE", headers: { Authorization: `Bearer ${token}` } }
      );
      if (response.status !== 200) throw new Error(await response.text());
      writeBlueprint(null);
    } catch (err) {
      addToast({
        type: "error",
        message: `The drawing could not be removed: ${err}`,
        keep: true,
      });
    }
  };

  return (
    <>
      <input
        type="file"
        accept="application/pdf"
        ref={fileInput}
        onChange={(e) => e.target.files && upload(e.target.files)}
        style={{ display: "none" }}
        multiple={false}
        onClick={(event) => {
          event.stopPropagation();
          (event.target as HTMLInputElement).value = "";
        }}
      />
      <Button
        size="small"
        endIcon={<ArrowDropDown color={blueprint ? "info" : "primary"} />}
        onClick={(event) => {
          event.stopPropagation();
          setAnchorEl(event.currentTarget);
        }}
        disabled={disabled || !details}
      >
        <PictureAsPdf color={blueprint ? "info" : "primary"} />
      </Button>
      <Menu
        anchorEl={anchorEl}
        open={Boolean(anchorEl)}
        onClose={() => setAnchorEl(null)}
        onClick={(event) => event.stopPropagation()}
      >
        {blueprint ? (
          [
            <MenuItem
              key="download"
              onClick={() => {
                setAnchorEl(null);
                fetchPDFFile(token, blueprint, productId);
              }}
            >
              Download {blueprint}
            </MenuItem>,
            <MenuItem
              key="remove"
              onClick={() => {
                setAnchorEl(null);
                if (
                  window.confirm(
                    "Are you sure you want to remove the pdf? This will remove it from the product itself. This cannot be undone."
                  )
                ) {
                  remove();
                }
              }}
            >
              Remove pdf
            </MenuItem>,
          ]
        ) : (
          <MenuItem
            onClick={() => {
              setAnchorEl(null);
              fileInput.current?.click();
            }}
          >
            Upload PDF
          </MenuItem>
        )}
      </Menu>
    </>
  );
};

export default BlueprintMenu;
